import React, { useState, useEffect } from 'react';
import { useUserContext } from '../config/UserContext';
import { useKeycloak } from '../config/KeycloakContext';
import { TrashIcon, PlusIcon, PencilIcon } from '@heroicons/react/24/outline';
import AddressInput from './AddressInput';

interface PortfolioBuilderCardProps {
  config: any;
  document: any;
  setDocument?: any;
  onContinue: () => void;
  onBack?: () => void;
}

const generateId = () => Math.random().toString(36).substring(2, 9);

/** County JSON field keys that are stored in a real column (first key = canonical name). */
const COLUMN_KEYS: Record<string, string[]> = {
  legalDescription: ['legalDescription', 'legal', 'legal_description'],
  abbreviatedLegalDescription: ['abbreviatedLegalDescription', 'briefLegal', 'abbreviated_legal'],
  address: ['address', 'address1', 'propertyAddress', 'property_address'],
  street1: ['street1'],
  street2: ['street2'],
  city: ['city'],
  zipcode: ['zipcode', 'zip'],
};
/** Parcel-level key: the first one found is sent as parcelNumber, and the key stays in the indexes too. */
const PARCEL_NUMBER_KEYS = ['parcelNumber', 'parcelNum', 'pid', 'apn', 'pin', 'parcelId', 'upi'];

/** Parse a single full-address string (e.g. "123 Main St, Leavenworth, KS 66048") into atomic parts if present. */
const parseFullAddressParts = (full: string): { street1: string; city?: string; zipcode?: string } => {
  const trimmed = (full || '').trim();
  if (!trimmed.includes(',')) return { street1: trimmed };
  const parts = trimmed.split(',').map(p => p.trim()).filter(Boolean);
  if (parts.length < 2) return { street1: trimmed };
  const street1 = parts[0];
  const last = parts[parts.length - 1];
  const zipMatch = last.match(/\b(\d{5}(?:-\d{4})?)\b/);
  const zipcode = zipMatch ? zipMatch[1] : undefined;
  let city: string | undefined;
  if (parts.length >= 3) {
    city = parts[parts.length - 2];
  } else {
    const withoutZip = last.replace(/\b\d{5}(?:-\d{4})?\b/, '').replace(/\b[A-Z]{2}\b/i, '').trim();
    if (withoutZip) city = withoutZip;
  }
  return { street1: street1 || trimmed, city, zipcode };
};


export const PortfolioBuilderCard: React.FC<PortfolioBuilderCardProps> = ({ config, document, onContinue, onBack }) => {
  const { apiBaseUrl, setParcels, freeze, setFreeze } = useUserContext();
  const { getToken } = useKeycloak();

  const instrumentConfig = config?.instrument || { addNodeLabel: "Add Deed/Auditor #", fields: [] };
  const parcelConfig = config?.parcel || { addNodeLabel: "Add Parcel", fields: [] };

  const hasInstrumentFields = instrumentConfig?.enabled !== false && Array.isArray(instrumentConfig?.fields) && instrumentConfig.fields.length > 0;
  const hasParcelFields = parcelConfig?.enabled !== false && Array.isArray(parcelConfig?.fields) && parcelConfig.fields.length > 0;

  const allowMultipleInstruments = hasInstrumentFields && (
    config?.allowMultipleInstruments === true ||
    instrumentConfig?.allowMultiple === true ||
    (config?.allowMultipleInstruments !== false && instrumentConfig?.allowMultiple !== false)
  );
  const allowMultipleParcels = hasParcelFields && (
    parcelConfig?.allowMultipleParcels === true ||
    parcelConfig?.allowMultiple === true ||
    (parcelConfig?.allowMultipleParcels !== false && parcelConfig?.allowMultiple !== false)
  );
  
  const [instruments, setInstruments] = useState<any[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [draftInstrument, setDraftInstrument] = useState<any>(null);

  const createEmptyDraftInstrument = () => ({
    id: generateId(),
    fields: {},
    parcels: (hasParcelFields && allowMultipleInstruments) ? [{ id: generateId(), fields: {} }] : [],
  });

  // Auto-open single draft for simple mode
  useEffect(() => {
    if (loaded && !allowMultipleInstruments && !draftInstrument) {
      if (instruments.length > 0) {
        setDraftInstrument(instruments[0]);
      } else {
        setDraftInstrument(createEmptyDraftInstrument());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowMultipleInstruments, instruments, draftInstrument, loaded, hasParcelFields]);

  // Compute global tract numbers
  const getGlobalTractNumber = (instId: string, parcelId: string) => {
    let tractCounter = 1;
    // Iterate saved instruments
    for (const inst of instruments) {
      if (inst.id === instId) break;
      tractCounter += (inst.parcels || []).length;
    }
    // Iterate active draft (which might be the target instrument, or a new one)
    const targetInst = draftInstrument?.id === instId ? draftInstrument : instruments.find(i => i.id === instId);
    if (!targetInst) return tractCounter;
    
    for (const p of targetInst.parcels || []) {
      if (p.id === parcelId) return tractCounter;
      tractCounter++;
    }
    return tractCounter;
  };

  /**
   * The county JSON decides which fields to ask; this decides where they are stored.
   * Fields that belong to a real column (address, legal description, ...) are sent as columns,
   * every other field stays in the instrument's / parcel's recording indexes.
   */
  const toPayload = (fields: Record<string, any>, isParcel: boolean) => {
    const indexes: Record<string, any> = { ...fields };
    const out: Record<string, any> = {};
    for (const [column, keys] of Object.entries(COLUMN_KEYS)) {
      let value = '';
      for (const k of keys) {
        const v = indexes[k];
        if (!value && v !== undefined && v !== null && String(v).trim() !== '') value = String(v);
        delete indexes[k];
      }
      if (value) out[column] = value;
    }
    // When a single full-address field is used (no separate city/zipcode inputs), store full string in address and atomic parts in street1/city/zipcode
    const fullAddr = out.address || out.street1;
    if (fullAddr && !out.city && !out.zipcode) {
      out.address = fullAddr;
      const parsed = parseFullAddressParts(fullAddr);
      out.street1 = parsed.street1;
      if (parsed.city) out.city = parsed.city;
      if (parsed.zipcode) out.zipcode = parsed.zipcode;
    }
    if (isParcel) {
      let number = '';
      const roleParcelField = (parcelConfig.fields || []).find((f: any) => f.displayRole === 'parcel-number');
      const candidateKeys = roleParcelField?.key
        ? [roleParcelField.key, ...PARCEL_NUMBER_KEYS]
        : PARCEL_NUMBER_KEYS;
      for (const k of candidateKeys) {
        const v = indexes[k];
        if (!number && v !== undefined && v !== null && String(v).trim() !== '') number = String(v);
      }
      if (number) out.parcelNumber = number;
    }
    for (const k of Object.keys(indexes)) {
      const v = indexes[k];
      if (k === 'id' || v === undefined || v === null || String(v).trim() === '') delete indexes[k];
    }
    out.recordingIndexes = indexes;
    return out;
  };

  /** Rebuild the form fields (config key names) from a stored deed / parcel row. Strictly scoped to configFields for that level. */
  const toFields = (row: any, configFields: any[], isParcel: boolean) => {
    const configKeys = new Set((configFields || []).map((f: any) => f.key));
    const rawIndexes = row?.recordingIndexes || {};
    const fields: Record<string, any> = {};
    for (const [k, v] of Object.entries(rawIndexes)) {
      if (configKeys.has(k) && v !== undefined && v !== null && v !== '') {
        fields[k] = v;
      }
    }
    const place = (keys: string[], value: any) => {
      if (value === undefined || value === null || value === '') return;
      const target = keys.filter((k) => configKeys.has(k));
      for (const k of target) fields[k] = value;
    };
    for (const [column, keys] of Object.entries(COLUMN_KEYS)) place(keys, row?.[column]);
    // If the form uses street1 or address as a single full-address input (no separate city/zipcode inputs), prefer the fuller of address/street1
    if (!configKeys.has('city') && !configKeys.has('zipcode') && !configKeys.has('zip')) {
      const fullAddr = row?.address && (!row?.street1 || String(row.address).length > String(row.street1).length)
        ? row.address
        : (row?.street1 || row?.address);
      if (fullAddr) {
        if (configKeys.has('street1')) fields.street1 = fullAddr;
        if (configKeys.has('address')) fields.address = fullAddr;
      }
    }
    if (isParcel && row?.parcelNumber) {
      const roleParcelField = (configFields || []).find((f: any) => f.displayRole === 'parcel-number');
      const targetParcelKeys = roleParcelField?.key ? [roleParcelField.key, ...PARCEL_NUMBER_KEYS] : PARCEL_NUMBER_KEYS;
      if (!targetParcelKeys.some((k) => fields[k])) {
        place(targetParcelKeys, row.parcelNumber);
      }
    }
    return fields;
  };

  const toInstruments = (deeds: any[]) =>
    (deeds || []).map((d: any) => {
      const deedFields = toFields(d, instrumentConfig.fields, false);
      if (!hasParcelFields) {
        return {
          id: generateId(),
          dbId: d.id,
          fields: deedFields,
          parcels: [],
        };
      }
      const mappedParcels = (d.parcels || []).map((p: any) => ({
        id: generateId(),
        dbId: p.id,
        fields: toFields(p, parcelConfig.fields, true),
      }));
      return {
        id: generateId(),
        dbId: d.id,
        fields: deedFields,
        parcels: mappedParcels.length > 0 ? mappedParcels : (allowMultipleInstruments ? [{ id: generateId(), fields: {} }] : []),
      };
    });

  const authHeaders = async () => {
    const token = getToken ? await getToken() : null;
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  };

  // Load what is already stored for this freeze (deeds -> parcels)
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        if (!document?.freezeLookup || !apiBaseUrl) return;
        const res = await fetch(`${apiBaseUrl}/api/frozendeed/freeze/${document.freezeLookup}/deeds`, { headers: await authHeaders() });
        const body = await res.json();
        if (!cancelled && body?.success) {
          const loadedInstruments = toInstruments(body.data?.deeds || []);
          if (loadedInstruments.length > 0) setInstruments(loadedInstruments);
        }
      } catch (err) {
        console.error('Error loading deeds:', err);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document?.freezeLookup, apiBaseUrl]);

  const saveToBackend = async (finalInstruments: any[]): Promise<boolean> => {
    try {
      const headers = await authHeaders();
      const deeds = finalInstruments.map((inst: any) => ({
        id: inst.dbId || undefined,
        ...toPayload(inst.fields || {}, false),
        parcels: (inst.parcels || []).map((p: any) => ({
          id: p.dbId || undefined,
          ...toPayload(p.fields || {}, true),
        })),
      }));

      const res = await fetch(`${apiBaseUrl}/api/frozendeed/freeze/${document.freezeLookup}/deeds`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ deeds }),
      });
      const body = await res.json();
      if (!body?.success) {
        console.error('Error saving portfolio:', body?.message);
        return false;
      }

      // Keep the database ids so the next save updates the same rows
      const saved: any[] = body.data?.deeds || [];
      finalInstruments.forEach((inst: any, i: number) => {
        inst.dbId = saved[i]?.id;
        (inst.parcels || []).forEach((p: any, j: number) => { p.dbId = saved[i]?.parcels?.[j]?.id; });
      });
      setInstruments([...finalInstruments]);

      const savedParcels: any[] = [];
      saved.forEach((d: any) => {
        if (Array.isArray(d.parcels) && d.parcels.length > 0) {
          savedParcels.push(...d.parcels);
        }
      });
      if (setParcels) setParcels(savedParcels);
      if (freeze && setFreeze) {
        setFreeze({
          ...freeze,
          parcels: savedParcels,
          countyIndexes: saved.map(({ parcels: _parcels, ...deed }: any) => deed),
        } as any);
      }
      return true;
    } catch (err) {
      console.error('Error saving portfolio:', err);
      return false;
    }
  };

  const handleContinue = async () => {
    let finalData = instruments;
    if (!allowMultipleInstruments && draftInstrument) {
      finalData = [draftInstrument];
      setInstruments(finalData);
    }
    const ok = await saveToBackend(finalData);
    if (!ok) {
      window.alert('We could not save your property information. Please try again.');
      return;
    }
    onContinue();
  };

  // --- Draft Form Helpers ---
  const updateDraftField = (key: string, value: string) => {
    setDraftInstrument((prev: any) => prev ? { ...prev, fields: { ...prev.fields, [key]: value } } : prev);
  };

  const addDraftParcel = () => {
    setDraftInstrument((prev: any) => prev ? {
      ...prev,
      parcels: [...(prev.parcels || []), { id: generateId(), fields: {} }]
    } : prev);
  };

  const updateDraftParcelField = (parcelId: string, key: string, value: string) => {
    setDraftInstrument((prev: any) => {
      if (!prev) return prev;
      const updatedParcels = prev.parcels.map((p: any) => 
        p.id === parcelId ? { ...p, fields: { ...p.fields, [key]: value } } : p
      );
      return { ...prev, parcels: updatedParcels };
    });
  };

  const updateDraftParcelFields = (parcelId: string, updates: Record<string, string>) => {
    setDraftInstrument((prev: any) => {
      if (!prev) return prev;
      const updatedParcels = prev.parcels.map((p: any) => 
        p.id === parcelId ? { ...p, fields: { ...p.fields, ...updates } } : p
      );
      return { ...prev, parcels: updatedParcels };
    });
  };

  const removeDraftParcel = (parcelId: string) => {
    setDraftInstrument((prev: any) => prev ? {
      ...prev,
      parcels: (prev.parcels || []).filter((p: any) => p.id !== parcelId)
    } : prev);
  };

  const isDraftValid = () => {
    if (!draftInstrument) return false;
    if (hasInstrumentFields) {
      for (const field of instrumentConfig.fields || []) {
        if (field.required && !draftInstrument.fields[field.key]) return false;
      }
    }
    if (hasParcelFields) {
      if (allowMultipleInstruments && (!draftInstrument.parcels || draftInstrument.parcels.length === 0)) return false;
      for (const parcel of draftInstrument.parcels || []) {
        for (const field of parcelConfig.fields || []) {
          if (field.required && !parcel.fields[field.key]) return false;
        }
      }
    }
    return true;
  };

  const saveDraftToModal = () => {
    if (!isDraftValid()) return;
    const isExisting = instruments.some(i => i.id === draftInstrument.id);
    let updated;
    if (isExisting) {
      updated = instruments.map(i => i.id === draftInstrument.id ? draftInstrument : i);
    } else {
      updated = [...instruments, draftInstrument];
    }
    setInstruments(updated);
    setDraftInstrument(null);
  };

  const renderField = (field: any, value: string, onChange: (val: string) => void, parcelId?: string) => {
    const ownCopyValue = field.provideOwnCopyText || 'See attached Schedule A';
    const isOwnCopy = value === ownCopyValue;
    
    const ownCopyCheckbox = field.allowProvideOwnCopy ? (
      <label className="flex items-center gap-2 mt-2 text-[14px] font-plus-jakarta text-[#485D7D] cursor-pointer">
        <input 
          type="checkbox" 
          className="w-4 h-4 rounded border-gray-300 text-[#0B4BB6] focus:ring-[#0B4BB6]"
          checked={isOwnCopy} 
          onChange={(e) => {
            if (e.target.checked) onChange(ownCopyValue);
            else onChange('');
          }} 
        />
        I will provide my own copy (Attach as Schedule A)
      </label>
    ) : null;

    let inputNode;
    if (field.type === 'textarea') {
      inputNode = (
        <textarea
          value={value}
          onChange={e => onChange(e.target.value)}
          disabled={isOwnCopy}
          className={`w-full px-4 py-2 rounded-lg border border-[#D7DFEC] font-plus-jakarta focus:outline-none focus:ring-2 focus:ring-[#0B4BB6] min-h-[80px] ${isOwnCopy ? 'bg-gray-100 opacity-75 cursor-not-allowed text-gray-500' : ''}`}
          placeholder={`Enter ${field.label.toLowerCase()}`}
        />
      );
    } else if (field.type === 'address') {
      inputNode = (
        <div className={isOwnCopy ? 'opacity-75 pointer-events-none' : ''}>
          <AddressInput
            value={value}
            onChange={onChange}
            placeholder="Search or enter full address"
          />
        </div>
      );
    } else {
      inputNode = (
        <input
          type={field.type === 'email' ? 'email' : 'text'}
          value={value}
          onChange={e => onChange(e.target.value)}
          disabled={isOwnCopy}
          className={`w-full px-4 py-2 rounded-lg border border-[#D7DFEC] font-plus-jakarta focus:outline-none focus:ring-2 focus:ring-[#0B4BB6] ${isOwnCopy ? 'bg-gray-100 opacity-75 cursor-not-allowed text-gray-500' : ''}`}
          placeholder={`Enter ${field.label.toLowerCase()}`}
        />
      );
    }

    return (
      <div className="w-full">
        {inputNode}
        {ownCopyCheckbox}
      </div>
    );
  };

  const renderUnifiedForm = (isModal: boolean = false) => {
    if (!draftInstrument) return null;

    const content = (
      <div className="space-y-8">
        {/* Instrument Fields */}
        {hasInstrumentFields && (
          <div className="space-y-4">
            {instrumentConfig.fields?.map((field: any) => (
              <div key={field.key} className="flex flex-col gap-1.5">
                <label className="text-sm font-plus-jakarta font-semibold text-[#141435]">
                  {field.label} {field.required && <span className="text-red-500">*</span>}
                </label>
                {renderField(field, draftInstrument.fields[field.key] || '', val => updateDraftField(field.key, val))}
              </div>
            ))}
          </div>
        )}

        {/* Parcels List — only rendered when the county step config defines parcel fields */}
        {hasParcelFields && (
          <div className="space-y-4">
            {allowMultipleInstruments && allowMultipleParcels && (
              <div className="flex items-center justify-between border-b border-gray-200 pb-2">
                <h3 className="font-plus-jakarta font-bold text-[#141435] text-lg">Parcels</h3>
              </div>
            )}
            
            {(!draftInstrument.parcels || draftInstrument.parcels.length === 0) ? (
              allowMultipleInstruments ? (
                <p className="text-sm text-gray-500 italic">No parcels attached.</p>
              ) : null
            ) : (
              <div className="space-y-4">
                {draftInstrument.parcels.map((parcel: any) => (
                  <div key={parcel.id} className={`relative ${allowMultipleParcels ? 'bg-gray-50 border border-gray-200 rounded-xl p-5 shadow-sm' : ''}`}>
                    {allowMultipleParcels && (!allowMultipleInstruments || draftInstrument.parcels.length > 1) && (
                      <button 
                        type="button"
                        onClick={() => removeDraftParcel(parcel.id)}
                        className="absolute top-3 right-3 p-1.5 text-gray-400 hover:text-red-500 hover:bg-white rounded-md transition-colors"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    )}
                    {allowMultipleInstruments && allowMultipleParcels && (
                      <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4">
                        Tract {getGlobalTractNumber(draftInstrument.id, parcel.id)}
                      </h4>
                    )}
                    
                    <div className="space-y-4 pr-6">
                      {parcelConfig.fields?.map((field: any) => (
                        <div key={field.key} className="flex flex-col gap-1.5">
                          <label className="text-sm font-plus-jakarta font-semibold text-[#141435]">
                            {field.label} {field.required && <span className="text-red-500">*</span>}
                          </label>
                          {renderField(field, parcel.fields[field.key] || '', val => updateDraftParcelField(parcel.id, field.key, val), parcel.id)}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {allowMultipleParcels && (
              <button
                type="button"
                onClick={addDraftParcel}
                className="mt-2 inline-flex items-center gap-2 text-sm text-[#0B4BB6] font-semibold hover:underline"
              >
                <PlusIcon className="w-5 h-5" />
                {parcelConfig.addNodeLabel || "Add Parcel"}
              </button>
            )}
          </div>
        )}
      </div>
    );

    if (isModal) {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-8">
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col max-h-full">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50 shrink-0">
              <h2 className="font-plus-jakarta font-bold text-[#141435] text-lg">
                {instruments.some(i => i.id === draftInstrument.id) ? 'Edit Deed' : instrumentConfig.addNodeLabel}
              </h2>
              <button onClick={() => setDraftInstrument(null)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
              {content}
            </div>
            
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3 shrink-0">
              <button 
                onClick={() => setDraftInstrument(null)}
                className="px-5 py-2 rounded-full font-plus-jakarta font-semibold text-gray-600 hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={saveDraftToModal}
                disabled={!isDraftValid()}
                className={`px-6 py-2 rounded-full font-plus-jakarta font-semibold text-white transition-colors ${!isDraftValid() ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#0B4BB6] hover:bg-blue-800'}`}
              >
                Save Deed
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="w-full">
        {content}
        <div className="flex flex-row w-full gap-4 mt-10">
          {onBack && (
            <button
              type="button"
              className="flex-1 bg-[#7C8B9D] text-white font-plus-jakarta font-bold text-[15px] py-[12px] px-5 rounded-full hover:bg-[#485D7D] transition-colors"
              onClick={onBack}
            >
              Back
            </button>
          )}
          <button
            type="button"
            className={`flex-1 text-white font-plus-jakarta font-bold text-[15px] py-[12px] px-5 rounded-full transition-colors ${!isDraftValid() ? 'bg-[#0B4BB6] opacity-50 cursor-not-allowed' : 'bg-[#0B4BB6] hover:bg-[#23235a]'}`}
            disabled={!isDraftValid()}
            onClick={handleContinue}
          >
            Save & Continue
          </button>
        </div>
      </div>
    );
  };

  // --- Render List View (Multiple Instruments) ---
  if (!allowMultipleInstruments) {
    return renderUnifiedForm(false);
  }

  return (
    <div className="w-full">
      <div className="space-y-6">
        {instruments.length === 0 ? (
          <div className="text-center py-10 bg-gray-50 border border-dashed border-gray-300 rounded-xl">
            <p className="text-gray-500 font-plus-jakarta mb-4">No instruments added yet.</p>
            <button
              onClick={() => setDraftInstrument(createEmptyDraftInstrument())}
              className="inline-flex items-center gap-2 bg-[#0B4BB6] text-white px-5 py-2.5 rounded-full font-plus-jakarta font-semibold hover:bg-blue-800 transition-colors"
            >
              <PlusIcon className="w-5 h-5" />
              {instrumentConfig.addNodeLabel || "Add Deed"}
            </button>
          </div>
        ) : (
          <>
            {instruments.map((inst, idx) => {
              const extraDeedFields = (instrumentConfig.fields || []).slice(1).filter((f: any) => {
                const v = inst.fields?.[f.key];
                return v !== undefined && v !== null && String(v).trim() !== '';
              });
              return (
              <div key={inst.id} className="border border-[#D7DFEC] rounded-xl overflow-hidden shadow-sm">
                <div className="bg-[#F8FAFC] px-5 py-4 border-b border-[#D7DFEC] flex justify-between items-start">
                  <div className="space-y-1">
                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wider block">Reference #{idx + 1}</span>
                    <h3 className="font-plus-jakarta font-bold text-[#141435] text-lg">
                      {inst.fields[instrumentConfig.fields?.[0]?.key] || 'Unnamed Reference'}
                    </h3>
                    {extraDeedFields.map((f: any) => (
                      <div key={f.key} className="text-sm text-[#485D7D]">
                        <span className="font-semibold text-[#141435]">{f.label}:</span> {inst.fields[f.key]}
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setDraftInstrument(inst)} className="p-2 text-gray-400 hover:text-blue-600 transition-colors">
                      <PencilIcon className="w-5 h-5" />
                    </button>
                    <button onClick={() => setInstruments(instruments.filter(i => i.id !== inst.id))} className="p-2 text-gray-400 hover:text-red-500 transition-colors">
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                
                {hasParcelFields && (
                  <div className="p-5 bg-white">
                    {(!inst.parcels || inst.parcels.length === 0) ? (
                      <p className="text-sm text-gray-500 italic">No parcels attached.</p>
                    ) : (
                      <div className="space-y-2">
                        {inst.parcels.map((parcel: any) => (
                          <div key={parcel.id} className="flex items-center gap-2 text-sm text-gray-700">
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                            <span className="font-semibold whitespace-nowrap">Tract {getGlobalTractNumber(inst.id, parcel.id)}:</span>
                            <span className="truncate">{parcel.fields[parcelConfig.fields?.[0]?.key] || 'Unnamed Parcel'}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              );
            })}
            
            <button
              onClick={() => setDraftInstrument(createEmptyDraftInstrument())}
              className="w-full py-4 border-2 border-dashed border-blue-200 text-blue-600 rounded-xl font-plus-jakarta font-semibold flex items-center justify-center gap-2 hover:bg-blue-50 transition-colors"
            >
              <PlusIcon className="w-5 h-5" />
              {instrumentConfig.addNodeLabel || "Add Deed"}
            </button>
          </>
        )}
      </div>

      <div className="flex flex-row w-full gap-4 mt-10">
        {onBack && (
          <button
            type="button"
            className="flex-1 bg-[#7C8B9D] text-white font-plus-jakarta font-bold text-[15px] py-[12px] px-5 rounded-full hover:bg-[#485D7D] transition-colors"
            onClick={onBack}
          >
            Back
          </button>
        )}
        <button
          type="button"
          className={`flex-1 text-white font-plus-jakarta font-bold text-[15px] py-[12px] px-5 rounded-full transition-colors ${instruments.length === 0 ? 'bg-[#0B4BB6] opacity-50 cursor-not-allowed' : 'bg-[#0B4BB6] hover:bg-[#23235a]'}`}
          disabled={instruments.length === 0}
          onClick={handleContinue}
        >
          Save & Continue
        </button>
      </div>

      {draftInstrument && renderUnifiedForm(true)}
    </div>
  );
};

export default PortfolioBuilderCard;
